/**
 * CircuitVersionTimeline — compact version chain for a single Circuit,
 * embedded inside an expanded CircuitCard.
 *
 * Mirrors `features/versions/components/VersionTimeline` but trimmed
 * for in-card use:
 *   * No upload-new-version CTA (the parent card / tab owns that).
 *   * No "open in chat" action (clicking the circuit name already
 *     navigates there).
 *   * Smaller paddings, no per-version description (the wire shape
 *     `CircuitVersionRow` doesn't carry one today).
 *
 * Driven by `useCircuitVersions(circuitId)` — the circuit-scoped chain
 * endpoint. Diff / restore / download still go through the project-
 * scoped version endpoints; we pass `circuit.project_id` (the owning
 * Board UUID) which the project router accepts as a valid scope.
 */
import { ConfirmDialog } from "@components/ConfirmDialog";
import { BranchVersionDialog } from "@features/projects/components/BranchVersionDialog";
import { SourceFormatChip } from "@features/versions/components/SourceFormatChip";
import { VersionBadge } from "@features/versions/components/VersionBadge";
import { VersionDiffView } from "@features/versions/components/VersionDiffView";
import {
  useRestoreVersion,
  versionDownloadUrl,
} from "@features/versions/hooks/useVersions";
import type { CircuitDTO, CircuitVersionRow } from "@models/circuit-ir";
import BranchIcon from "@mui/icons-material/CallSplitRounded";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import HistoryIcon from "@mui/icons-material/HistoryRounded";
import KicadIcon from "@mui/icons-material/IntegrationInstructionsRounded";
import RestoreIcon from "@mui/icons-material/RestoreRounded";
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useState } from "react";
import { exportKicadNetUrl } from "../hooks/useCircuitIr";
import { useCircuitVersions } from "../hooks/useCircuitLifecycle";

interface CircuitVersionTimelineProps {
  circuit: CircuitDTO;
}

export function CircuitVersionTimeline({
  circuit,
}: CircuitVersionTimelineProps) {
  const { data: versions, isLoading, error } = useCircuitVersions(circuit.id);
  const restore = useRestoreVersion(circuit.project_id);
  const [diffTarget, setDiffTarget] = useState<{
    fromVersion: string;
    toVersion: string;
  } | null>(null);
  const [restoreTarget, setRestoreTarget] = useState<CircuitVersionRow | null>(
    null,
  );
  const [branchTarget, setBranchTarget] = useState<CircuitVersionRow | null>(
    null,
  );

  if (isLoading) {
    return (
      <Box sx={{ py: 1.5, display: "flex", justifyContent: "center" }}>
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 1 }}>
        Failed to load versions: {(error as Error).message}
      </Alert>
    );
  }

  if (!versions || versions.length === 0) {
    return (
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={(t) => ({
          mt: 1,
          px: 1.25,
          py: 1,
          borderRadius: 1,
          color: "text.disabled",
          fontFamily: t.fonts.mono,
          fontSize: 11.5,
          letterSpacing: "0.04em",
          border: `1px dashed ${t.palette.border.default}`,
        })}
      >
        <HistoryIcon sx={{ fontSize: 14, opacity: 0.7 }} />
        <Box>No committed versions yet.</Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={0.75} sx={{ mt: 1 }}>
      <Typography
        variant="overline"
        sx={(t) => ({
          color: t.palette.editorial.main,
          fontSize: 10,
          letterSpacing: "0.16em",
          mb: 0.25,
        })}
      >
        Versions · {versions.length}
      </Typography>

      {versions.map((v, idx) => {
        const previous = versions[idx + 1];
        const isExpanded = diffTarget?.toVersion === v.id;

        return (
          <Paper
            key={v.id}
            variant="outlined"
            sx={(t) => ({
              px: 1.25,
              py: 1,
              borderColor: v.is_current
                ? alpha(t.palette.primary.main, 0.6)
                : t.palette.border.default,
              bgcolor: v.is_current ? "accent.softer" : "transparent",
            })}
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ minWidth: 0 }}
            >
              <VersionBadge
                versionNumber={v.version_number}
                semverLabel={v.semver_label}
                isCurrent={v.is_current}
                imported={v.imported}
              />
              <SourceFormatChip format={v.source_format} />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {formatRelative(v.uploaded_at)} · {v.component_count} comps ·{" "}
                {v.net_count} nets
              </Typography>

              <Stack direction="row" spacing={0.25} sx={{ flexShrink: 0 }}>
                {previous && (
                  <Tooltip
                    title={`Diff against ${
                      previous.semver_label?.trim() ||
                      `v${previous.version_number}`
                    }`}
                  >
                    <IconButton
                      size="small"
                      onClick={() =>
                        setDiffTarget(
                          isExpanded
                            ? null
                            : { fromVersion: previous.id, toVersion: v.id },
                        )
                      }
                      sx={{ color: isExpanded ? "primary.main" : undefined }}
                    >
                      <HistoryIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                {v.imported && (
                  <Tooltip title="Download source file">
                    <IconButton
                      size="small"
                      component="a"
                      href={versionDownloadUrl(circuit.project_id, v.id)}
                      rel="noopener"
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Export as KiCad netlist (.net)">
                  <IconButton
                    size="small"
                    component="a"
                    href={exportKicadNetUrl(v.id)}
                    target="_blank"
                    rel="noopener"
                  >
                    <KicadIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Branch as a candidate from this version">
                  <IconButton size="small" onClick={() => setBranchTarget(v)}>
                    <BranchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {!v.is_current && (
                  <Tooltip title="Set this version as current">
                    <IconButton
                      size="small"
                      onClick={() => setRestoreTarget(v)}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Stack>
            </Stack>

            {isExpanded && previous && (
              <Box sx={{ mt: 1 }}>
                <VersionDiffView
                  projectId={circuit.project_id}
                  // biome-ignore lint/style/noNonNullAssertion: isExpanded above requires diffTarget!=null
                  versionAId={diffTarget!.fromVersion}
                  // biome-ignore lint/style/noNonNullAssertion: same guard as above
                  versionBId={diffTarget!.toVersion}
                />
              </Box>
            )}
          </Paper>
        );
      })}

      <ConfirmDialog
        open={!!restoreTarget}
        title={`Restore ${
          restoreTarget?.semver_label?.trim() ||
          `v${restoreTarget?.version_number}`
        }?`}
        message={
          restoreTarget
            ? `This makes ${
                restoreTarget.semver_label?.trim() ||
                `v${restoreTarget.version_number}`
              } the current version for ${circuit.name}. Newer versions stay in the timeline and can be restored again.`
            : ""
        }
        confirmLabel="Restore"
        onConfirm={() => {
          if (restoreTarget) restore.mutate(restoreTarget.id);
          setRestoreTarget(null);
        }}
        onCancel={() => setRestoreTarget(null)}
      />

      <BranchVersionDialog
        open={!!branchTarget}
        projectName={circuit.name}
        sourceVersionLabel={
          branchTarget
            ? branchTarget.semver_label?.trim() ||
              `v${branchTarget.version_number}`
            : ""
        }
        sourceCircuitId={circuit.id}
        onClose={() => setBranchTarget(null)}
      />
    </Stack>
  );
}

function formatRelative(iso: string | null | undefined): string {
  if (!iso) return "Unknown";
  try {
    const date = new Date(iso);
    const diffMs = Date.now() - date.getTime();
    const mins = Math.round(diffMs / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.round(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.round(hours / 24);
    if (days < 30) return `${days}d ago`;
    return date.toLocaleDateString();
  } catch {
    return iso;
  }
}
